/**
 * World-space QWERTY keyboard drawn to one canvas texture on a single plane: one draw call
 * instead of ~100 three-mesh-ui meshes. Keys are hit-tested through the plane's UV. Works with
 * rays (controllers, mouse), finger presses (hand tracking) and the physical keyboard.
 */
import * as THREE from 'three';
import { PALETTE } from '../dashboards/charts.ts';
import type { InteractiveSystem } from './Interactive.ts';
import { ROWS_LETTERS, ROWS_SYMBOLS, type KeyDef } from './keyboardLayout.ts';

export interface KeyboardEvents {
  onInput(char: string): void;
  onBackspace(): void;
  onEnter(): void;
  onKeySound?(): void;
}

interface KeyRect {
  def: KeyDef;
  x: number;
  y: number;
  w: number;
  h: number;
}

const CANVAS_W = 1024;
const CANVAS_H = 464;
/** Keyboard colours from the panel palette, read at draw time (light in study rooms). */
const COLOURS = {
  get panel() {
    return PALETTE.bg;
  },
  get key() {
    return PALETTE.raised;
  },
  get command() {
    return PALETTE.raisedStrong;
  },
  get hover() {
    return PALETTE.raisedHover;
  },
  get pressed() {
    return PALETTE.tealDim;
  },
  get text() {
    return PALETTE.text;
  },
  get muted() {
    return PALETTE.muted;
  },
};

export class Keyboard {
  readonly group = new THREE.Group();
  readonly mesh: THREE.Mesh;
  private readonly canvas = document.createElement('canvas');
  private readonly ctx: CanvasRenderingContext2D;
  private readonly texture: THREE.CanvasTexture;
  private keys: KeyRect[] = [];
  private shift = false;
  private symbols = false;
  private hovered = -1;
  private pressed = -1;
  private pressedUntil = 0;
  enabled = true;

  constructor(
    system: InteractiveSystem,
    private readonly listeners: KeyboardEvents,
    readonly width = 0.95,
  ) {
    this.canvas.width = CANVAS_W;
    this.canvas.height = CANVAS_H;
    this.ctx = this.canvas.getContext('2d')!;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.anisotropy = 4;
    const height = (width * CANVAS_H) / CANVAS_W;
    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      new THREE.MeshBasicMaterial({ map: this.texture, transparent: true, toneMapped: false }),
    );
    this.mesh.name = 'keyboard';
    this.group.add(this.mesh);
    this.group.name = 'keyboard';
    this.layout();
    this.draw();
    system.add({
      object: this.mesh,
      touchable: true,
      onHover: (_p, point) => this.setHovered(this.keyAt(point)),
      onLeave: () => this.setHovered(-1),
      onSelect: (_p, point) => this.press(this.keyAt(point)),
    });
  }

  /* ---------- Layout and drawing ---------- */

  private layout(): void {
    const rows = this.symbols ? ROWS_SYMBOLS : ROWS_LETTERS;
    const margin = 6;
    const pad = 16;
    const rowH = (CANVAS_H - pad * 2) / rows.length;
    const units = 11.5;
    const unit = (CANVAS_W - pad * 2) / units;
    this.keys = [];
    rows.forEach((row, r) => {
      const totalUnits = row.reduce((s, k) => s + (k.width ?? 1), 0);
      let x = pad + ((units - totalUnits) * unit) / 2;
      for (const def of row) {
        const w = unit * (def.width ?? 1);
        this.keys.push({
          def,
          x: x + margin / 2,
          y: pad + r * rowH + margin / 2,
          w: w - margin,
          h: rowH - margin,
        });
        x += w;
      }
    });
  }

  private labelFor(def: KeyDef): string {
    if (def.input && def.input.length === 1 && /[a-z]/.test(def.input))
      return this.shift ? def.input.toUpperCase() : def.input;
    if (def.command === 'shift') return this.shift ? 'SHIFT' : 'shift';
    return def.label;
  }

  private draw(): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.fillStyle = COLOURS.panel;
    roundRect(ctx, 0, 0, CANVAS_W, CANVAS_H, 22);
    ctx.fill();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    this.keys.forEach((k, i) => {
      const isCommand = !!k.def.command;
      ctx.fillStyle =
        i === this.pressed
          ? COLOURS.pressed
          : i === this.hovered
            ? COLOURS.hover
            : isCommand
              ? COLOURS.command
              : COLOURS.key;
      roundRect(ctx, k.x, k.y, k.w, k.h, 10);
      ctx.fill();
      ctx.fillStyle = isCommand ? COLOURS.muted : COLOURS.text;
      ctx.font = `${isCommand ? 500 : 600} ${isCommand ? 26 : 34}px "Inter", "Segoe UI", Roboto, system-ui, sans-serif`;
      ctx.fillText(this.labelFor(k.def), k.x + k.w / 2, k.y + k.h / 2 + 2);
    });
    this.texture.needsUpdate = true;
  }

  /* ---------- Hit testing ---------- */

  private keyAt(worldPoint: THREE.Vector3): number {
    const local = this.mesh.worldToLocal(worldPoint.clone());
    const height = (this.width * CANVAS_H) / CANVAS_W;
    const px = (local.x / this.width + 0.5) * CANVAS_W;
    const py = (0.5 - local.y / height) * CANVAS_H;
    return this.keys.findIndex((k) => px >= k.x && px <= k.x + k.w && py >= k.y && py <= k.y + k.h);
  }

  private setHovered(index: number): void {
    if (index === this.hovered) return;
    this.hovered = index;
    this.draw();
  }

  private press(index: number): void {
    if (!this.enabled || index < 0) return;
    const def = this.keys[index]!.def;
    this.pressed = index;
    this.pressedUntil = performance.now() + 120;
    this.listeners.onKeySound?.();
    switch (def.command) {
      case 'shift':
        this.shift = !this.shift;
        break;
      case 'symbols':
        this.symbols = !this.symbols;
        this.shift = false;
        this.layout();
        this.hovered = -1;
        this.pressed = -1;
        break;
      case 'backspace':
        this.listeners.onBackspace();
        break;
      case 'space':
        this.listeners.onInput(' ');
        break;
      case 'enter':
        this.listeners.onEnter();
        break;
      default:
        if (def.input) {
          this.listeners.onInput(this.shift ? def.input.toUpperCase() : def.input);
          if (this.shift) this.shift = false;
        }
    }
    this.draw();
  }

  /** Call once per frame to clear the pressed highlight. */
  update(): void {
    if (this.pressed >= 0 && performance.now() > this.pressedUntil) {
      this.pressed = -1;
      this.draw();
    }
  }

  /** Forward a physical keyboard event (desktop fallback). Returns true when handled. */
  handleKeyEvent(e: KeyboardEvent): boolean {
    if (!this.enabled) return false;
    if (e.key === 'Backspace') {
      this.listeners.onBackspace();
      return true;
    }
    if (e.key === 'Enter') {
      this.listeners.onEnter();
      return true;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      this.listeners.onInput(e.key);
      return true;
    }
    return false;
  }

  /** World position of the centre of the key with this label (dev hooks). */
  keyWorld(label: string): THREE.Vector3 | undefined {
    const k = this.keys.find((x) => this.labelFor(x.def) === label || x.def.input === label);
    if (!k) return undefined;
    const height = (this.width * CANVAS_H) / CANVAS_W;
    const local = new THREE.Vector3(
      ((k.x + k.w / 2) / CANVAS_W - 0.5) * this.width,
      (0.5 - (k.y + k.h / 2) / CANVAS_H) * height,
      0.002,
    );
    this.mesh.updateWorldMatrix(true, false);
    return this.mesh.localToWorld(local);
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible;
    this.enabled = visible;
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
